import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { sendCampaignEmailWithAttachments } from "@/shared/actions/campanas/migracion.actions";

interface EmailPayload {
  to: string[];
  subject: string;
  message: string;
}

interface SendInternalSearchEmailModalProps {
  open: boolean;
  onClose: () => void;
  defaultMessage?: string;
  helperText?: string;
  validateBeforeSend?: () => string;
  onSendEmail?: (payload: EmailPayload) => Promise<string | void>;
}

export const SendInternalSearchEmailModal = ({
  open,
  onClose,
  defaultMessage = "Estimados,\n\nSe adjunta la información correspondiente a la búsqueda realizada.\n\nSaludos.",
  helperText = "",
  validateBeforeSend,
  onSendEmail,
}: SendInternalSearchEmailModalProps) => {
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("Resultados de búsqueda");
  const [message, setMessage] = useState(defaultMessage);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();

    if (!email) return;

    if (!isValidEmail(email)) {
      setErrorMessage("Ingrese un correo válido");
      return;
    }

    if (emails.includes(email)) {
      setErrorMessage("Ese correo ya fue agregado");
      return;
    }

    setEmails((prev) => [...prev, email]);
    setEmailInput("");
    setErrorMessage("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((email) => email !== emailToRemove));
  };

  const resetModal = () => {
    setEmailInput("");
    setEmails([]);
    setSubject("Resultados de búsqueda interna");
    setMessage(defaultMessage);
    setErrorMessage("");
  };

  const handleClose = () => {
    if (loading) return;
    resetModal();
    onClose();
  };

  const handleSendEmail = async () => {
    if (emails.length === 0) {
      setErrorMessage("Debe agregar al menos un correo destinatario");
      return;
    }

    if (!subject.trim()) {
      setErrorMessage("El asunto es obligatorio");
      return;
    }

    if (!message.trim()) {
      setErrorMessage("El mensaje es obligatorio");
      return;
    }

    const validationError = validateBeforeSend?.();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const payload = {
        to: emails,
        subject: subject.trim(),
        message: message.trim(),
      };

      const successMessage = onSendEmail
        ? await onSendEmail(payload)
        : await sendCampaignEmailWithAttachments({
            ...payload,
            files: [],
          }).then(() => undefined);

      toast.success(successMessage || "Correo enviado correctamente");
      resetModal();
      onClose();
    } catch (error: any) {
      const messageError =
        error?.response?.data?.message || "No se pudo enviar el correo";

      setErrorMessage(messageError);
      toast.error(messageError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: "90%",
          maxWidth: 620,
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          mx: "auto",
          my: "10vh",
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Enviar correo
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={1}>
              Envío de correo para resultados de búsqueda.
            </Typography>
          </Box>

          {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

          <Box display="flex" gap={1}>
            <TextField
              label="Correo destinatario"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setErrorMessage("");
              }}
              fullWidth
              size="small"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddEmail();
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleAddEmail}
              disabled={loading}
            >
              Agregar
            </Button>
          </Box>

          {emails.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {emails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => handleRemoveEmail(email)}
                  disabled={loading}
                />
              ))}
            </Box>
          )}

          <TextField
            label="Asunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            disabled={loading}
          />

          <TextField
            label="Mensaje"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            disabled={loading}
          />

          {helperText && (
            <Typography variant="body2" color="text.secondary">
              {helperText}
            </Typography>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSendEmail}
              disabled={loading}
            >
              {loading ? "Enviando..." : "Enviar correo"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
